import { DEPARTMENT_ID } from '@@types/common';
import Department from '@@types/Department';
import DepartmentResponse from '@@types/Department/Response';
import Member from '@@types/Member';

import { DEPARTMENT_MEMBERS_SIZE } from '~/constants';
import { WinNativeFocusTarget } from '~/constants/nativeApi';
import popupWindowService from '~/services/popupWindowService';
import { membersStore } from '~/stores/MembersStore';
import { organizationChartStore } from '~/stores/OrganizationChartStore';
import { api } from '~/stores/providers/Api2';
import { keyBy } from '~/utils/fnUtil/fnUtil';
import { getDefaultDepartmentId } from '~/utils/memberUtil';
import { lift, runFutureValue } from '~/utils/monadUtil';
import { getUrlByEnv, isElectron, isWinNative } from '~/utils/runEnvironmentUtil';
import { encaseP } from 'fluture';
import { computed } from 'mobx';
import { allPass, chain, find, head, identity, map, pipe, prop, tap, values, when } from 'ramda';
import assoc from 'ramda/es/assoc';
import curry from 'ramda/es/curry';
import juxt from 'ramda/es/juxt';
import pluck from 'ramda/es/pluck';
import propEq from 'ramda/es/propEq';
import reject from 'ramda/es/reject';

import Actions from '../Actions';
import { stores } from '../Stores';

class OrganizationChartActions {
  constructor(private actions: Actions) {}

  @computed
  get primaryDepartment() {
    return pipe<any, any, any>(
      find(allPass([prop('primaryFlag'), propEq('organizationId', stores.user.defaultOrganizationId)])),
      lift(identity)
    )(organizationChartStore.departments);
  }

  private addDepartment = (departmentResponse: DepartmentResponse) => {
    const { content: department, references } = departmentResponse;
    organizationChartStore.setDepartment(department);
    pipe<any, any, any, any, any, any>(
      lift(identity),
      map(prop('departmentMap')),
      map(values),
      map(head),
      map(organizationChartStore.setDepartment)
    )(references);
  };

  fetchDepartment = (departmentId: string) => {
    return pipe<DEPARTMENT_ID, any, any, any>(
      encaseP(api.department.fetchDepartment),
      map(tap(this.addDepartment)),
      map(prop('content'))
    )(departmentId);
  };

  fetchMembers = curry((page: number, departmentId: string) => {
    return pipe<any, any, any>(
      encaseP(api.department.fetchDepartmentMembers),
      map(juxt([this.setMemberCount(departmentId), this.mergeMembers(departmentId)]))
    )({ departmentId, page, size: DEPARTMENT_MEMBERS_SIZE });
  });

  fetchSubDepartments = (departmentId) => {
    pipe<any, any, any, any, any, any>(
      encaseP(api.department.fetchSubDepartments),
      map(reject(propEq('id', departmentId))),
      map(map(assoc('parentDepartmentId', departmentId))),
      map(juxt([this.setSubDepartment(departmentId), map(organizationChartStore.setDepartment)])),
      runFutureValue(identity)
    )(departmentId);
  };

  fetchUserDepartment = (user: Member) => {
    pipe<Member, any, any, any, any>(
      getDefaultDepartmentId,
      lift(identity),
      chain(this.fetchDepartment),
      runFutureValue(this.handleUserDepartment)
    )(user);
  };

  handleSelectDepartment = (departmentId: string) => {
    pipe<any, any, any, any>(
      this.fetchDepartment,
      map(prop('id')),
      runFutureValue(organizationChartStore.setActiveDepartmentId)
    )(departmentId);
    organizationChartStore.setShowPrimaryDepartments(false);
  };

  fetchPrimaryDepartments = () => {
    return api.department.fetchPrimaryDepartments().then(
      tap((departments: Department[]) => {
        const primaryDepartments = departments.filter(
          (department) => department.organizationType !== 'guest' && department.displayFlag
        );
        organizationChartStore.mergeDepartmentMap(keyBy('id', primaryDepartments));
        organizationChartStore.setPrimaryDepartmentIds(primaryDepartments.map(prop('id')));
      })
    );
  };

  private handleUserDepartment = ({ id }: Member) => {
    organizationChartStore.setActiveDepartmentId(id);
    organizationChartStore.setDefaultDepartmentId(id);
  };

  private mergeMembers = curry((departmentId, { contents }) => {
    return pipe<any, any, any, any>(
      membersStore.mergeMembers,
      pluck('id'),
      organizationChartStore.appendMembers(departmentId)
    )(contents);
  });

  private setMemberCount = curry((departmentId, { totalCount }) => {
    organizationChartStore.setDepartmentMemberCount(departmentId, totalCount);
  });

  private setSubDepartment = curry((parentDepartmentId, departments) => {
    return pipe<any, any, any>(
      pluck('id'),
      organizationChartStore.setSubDepartmentsId(parentDepartmentId)
    )(departments);
  });

  openOrganizationChart = () => {
    const ORG_CHART_WINDOW_NAME = 'ORG_CHART';
    const url = getUrlByEnv('ORG_CHART');
    if (isElectron()) {
      return popupWindowService.open(url, ORG_CHART_WINDOW_NAME, {
        toolbar: 'no',
        location: 'no',
        resizable: 'no',
        width: 1200,
        height: 800,
      });
    }

    const popupWindow = popupWindowService.open('', ORG_CHART_WINDOW_NAME, {
      toolbar: 'no',
      location: 'no',
      resizable: 'no',
      width: 1200,
      height: 800,
    });
    if (popupWindow && popupWindow.location.href === 'about:blank') {
      return popupWindowService.open(url, ORG_CHART_WINDOW_NAME, {
        toolbar: 'no',
        location: 'no',
        resizable: 'no',
        width: 1200,
        height: 800,
      });
    }

    popupWindow.name = ORG_CHART_WINDOW_NAME;
    isWinNative()
      ? this.actions.nativeActions.sendSetFocus({ target: WinNativeFocusTarget.ORG_CHART })
      : popupWindow.focus();
    return popupWindow;
  };

  // addDepartments = (departmentResponses: DepartmentResponse[]) => {
  //   map(this.addDepartment, departmentResponses);
  //   return departmentResponses;
  // };

  // private setActiveDepartmentIdFromResponse = (departmentResponses: DepartmentResponse[]) => {
  //   const { organizationChartStore } = this.stores;
  //   pipe(
  //     pluck('content'),
  //     find(prop('primaryFlag')),
  //     lift(identity),
  //     map(prop('id')),
  //     map(juxt([organizationChartStore.setActiveDepartmentId, organizationChartStore.setDefaultDepartmentId]))
  //   )(departmentResponses);
  //   return departmentResponses;
  // };

  // fetchDepartments = (departmentIds: string[]) => {
  //   if (isEmpty(departmentIds)) return;
  //   pipe(
  //     converge((count, futures) => parallel(count, futures), [length, map(encaseP(api.fetchDepartment))]),
  //     map(juxt([this.addDepartments, this.setActiveDepartmentIdFromResponse])),
  //     runFutureValue(identity)
  //   )(departmentIds);
  // };
}

export default OrganizationChartActions;
